import React, { useEffect, useState } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-sistema-bks.cloudfunctions.net';

const PLATFORMS = [
  { id: 'meta', icon: '📘', color: '#1877F2', enabled: true },
  { id: 'tiktok', icon: '🎵', color: '#000000', enabled: true },
  { id: 'google', icon: '🔍', color: '#EA4335', enabled: true }
];

export default function AdsConfig() {
  const { context } = useOrganization();
  const { t } = useTranslation('adsStudio');
  const [configs, setConfigs] = useState({ meta: null, tiktok: null, google: null });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const uid = getAuth().currentUser?.uid;
  const orgId = context.isOrganization ? context.orgId : null;
  const basePath = context.isOrganization
    ? `organizations/${context.orgId}`
    : `clientes/${uid}`;

  useEffect(() => {
    if (!uid) return;
    const unsubscribers = PLATFORMS.map(({ id }) => {
      const ref = doc(db, basePath, 'adsConfig', id);
      return onSnapshot(ref, (snap) => {
        setConfigs(prev => ({ ...prev, [id]: snap.exists() ? snap.data() : null }));
      }, () => {
        setConfigs(prev => ({ ...prev, [id]: null }));
      });
    });
    return () => unsubscribers.forEach(unsub => unsub());
  }, [uid, basePath]);

  const [googleCustomerIdInput, setGoogleCustomerIdInput] = useState('');

  const OAUTH_ROUTES = {
    meta: '/api/oauth/meta-ads/start',
    tiktok: '/api/oauth/tiktok-ads/start',
    google: '/api/oauth/google-ads/start'
  };

  const DISCONNECT_FUNCTIONS = {
    meta: 'metaAdsDisconnect',
    tiktok: 'tiktokAdsDisconnect',
    google: 'googleAdsDisconnect'
  };

  const handleConnect = async (platformId) => {
    const route = OAUTH_ROUTES[platformId];
    if (!route) return;
    setConnecting(true);
    try {
      const idToken = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}${route}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ organizationId: orgId })
      });
      const data = await response.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (err) {
      console.error('Connect error:', err);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (platformId) => {
    const funcName = DISCONNECT_FUNCTIONS[platformId];
    if (!funcName) return;
    if (!window.confirm(t(`config.${platformId}.disconnect`) + '?')) return;
    setDisconnecting(true);
    try {
      const call = httpsCallable(functions, funcName);
      await call({ organizationId: orgId });
    } catch (err) {
      console.error('Disconnect error:', err);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSelectAccount = async (adAccountId) => {
    try {
      const call = httpsCallable(functions, 'metaAdsSelectAccount');
      await call({ organizationId: orgId, adAccountId });
    } catch (err) {
      console.error('Select account error:', err);
    }
  };

  const handleSelectPage = async (pageId) => {
    try {
      const call = httpsCallable(functions, 'metaAdsSelectAccount');
      await call({ organizationId: orgId, pageId });
    } catch (err) {
      console.error('Select page error:', err);
    }
  };

  const handleSelectTikTokAccount = async (advertiserId) => {
    try {
      const call = httpsCallable(functions, 'tiktokAdsSelectAccount');
      await call({ organizationId: orgId, advertiserId });
    } catch (err) {
      console.error('Select TikTok account error:', err);
    }
  };

  const handleSaveGoogleCustomerId = async () => {
    const normalized = googleCustomerIdInput.replace(/-/g, '');
    if (!/^\d{10}$/.test(normalized)) {
      alert(t('config.google.invalidCustomerId'));
      return;
    }
    try {
      const call = httpsCallable(functions, 'googleAdsSelectAccount');
      await call({ organizationId: orgId, customerId: normalized });
      setGoogleCustomerIdInput('');
    } catch (err) {
      console.error('Save Google customer ID error:', err);
    }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 0.25rem 0', fontSize: '1.3rem' }}>{t('config.title')}</h2>
      <p style={{ margin: '0 0 1.5rem 0', color: '#666', fontSize: '0.9rem' }}>{t('config.subtitle')}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        {PLATFORMS.map(({ id, icon, color, enabled }) => {
          const config = configs[id];
          const isConnected = config?.status === 'connected';
          const tokenExpiring = isConnected && config?.tokenExpiresAt?.seconds &&
            (config.tokenExpiresAt.seconds * 1000 - Date.now()) < 7 * 24 * 60 * 60 * 1000;

          return (
            <div key={id} style={{
              backgroundColor: '#fff', borderRadius: '12px', padding: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: `1px solid ${isConnected ? '#d4edda' : '#e8e8e8'}`,
              display: 'flex', flexDirection: 'column', gap: '0.75rem'
            }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{
                  fontSize: '1.5rem', width: '40px', height: '40px', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  backgroundColor: `${color}15`, borderRadius: '8px'
                }}>{icon}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem' }}>{t(`config.${id}.name`)}</h3>
                  <span style={{
                    fontSize: '0.75rem', fontWeight: '600',
                    color: isConnected ? '#155724' : '#6c757d',
                    backgroundColor: isConnected ? '#d4edda' : '#e2e3e5',
                    padding: '2px 8px', borderRadius: '4px'
                  }}>
                    {isConnected ? t(`config.${id}.connected`) : t(`config.${id}.disconnected`)}
                  </span>
                </div>
              </div>

              <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', lineHeight: '1.4' }}>
                {t(`config.${id}.description`)}
              </p>

              {/* Token expiring warning */}
              {tokenExpiring && (
                <div style={{ padding: '0.5rem', backgroundColor: '#fff3cd', borderRadius: '6px', fontSize: '0.8rem', color: '#856404' }}>
                  {t(`config.${id}.tokenExpiring`)}
                </div>
              )}

              {/* Connected: show date + connected by */}
              {isConnected && config?.connectedAt && (
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>
                  {config.connectedByName && `${config.connectedByName} — `}
                  {new Date(config.connectedAt.seconds * 1000).toLocaleDateString()}
                </p>
              )}

              {/* Meta: Ad Account selector */}
              {id === 'meta' && isConnected && config?.adAccounts?.length > 1 && (
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '500', color: '#333', display: 'block', marginBottom: '0.25rem' }}>
                    {t('config.meta.adAccount')}
                  </label>
                  <select
                    value={config.selectedAdAccountId || ''}
                    onChange={(e) => handleSelectAccount(e.target.value)}
                    style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                  >
                    <option value="">{t('config.meta.noAccountSelected')}</option>
                    {config.adAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.accountId})</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Meta: Page selector */}
              {id === 'meta' && isConnected && config?.pages?.length > 1 && (
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '500', color: '#333', display: 'block', marginBottom: '0.25rem' }}>
                    {t('config.meta.page')}
                  </label>
                  <select
                    value={config.selectedPageId || ''}
                    onChange={(e) => handleSelectPage(e.target.value)}
                    style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                  >
                    {config.pages.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* TikTok: Advertiser Account selector */}
              {id === 'tiktok' && isConnected && config?.advertiserAccounts?.length > 1 && (
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '500', color: '#333', display: 'block', marginBottom: '0.25rem' }}>
                    {t('config.tiktok.advertiserAccount')}
                  </label>
                  <select
                    value={config.selectedAdvertiserId || ''}
                    onChange={(e) => handleSelectTikTokAccount(e.target.value)}
                    style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                  >
                    <option value="">{t('config.tiktok.noAccountSelected')}</option>
                    {config.advertiserAccounts.map(a => (
                      <option key={a.advertiserId} value={a.advertiserId}>{a.name} ({a.advertiserId})</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Google: Customer ID selector or manual input */}
              {id === 'google' && isConnected && (
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '500', color: '#333', display: 'block', marginBottom: '0.25rem' }}>
                    {t('config.google.customerId')}
                  </label>
                  {config?.customerIds?.length > 1 ? (
                    <select
                      value={config.selectedCustomerId || ''}
                      onChange={(e) => {
                        const call = httpsCallable(functions, 'googleAdsSelectAccount');
                        call({ organizationId: orgId, customerId: e.target.value });
                      }}
                      style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                    >
                      <option value="">{t('config.google.noAccountSelected')}</option>
                      {config.customerIds.map(cid => (
                        <option key={cid} value={cid}>{cid.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        value={config.selectedCustomerId
                          ? config.selectedCustomerId.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
                          : googleCustomerIdInput}
                        onChange={(e) => setGoogleCustomerIdInput(e.target.value)}
                        placeholder={t('config.google.customerIdPlaceholder')}
                        disabled={!!config.selectedCustomerId}
                        style={{ flex: 1, padding: '0.4rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '0.85rem' }}
                      />
                      {!config.selectedCustomerId && (
                        <button onClick={handleSaveGoogleCustomerId} style={{
                          padding: '0.4rem 0.75rem', backgroundColor: '#1a73e8', color: '#fff',
                          border: 'none', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer'
                        }}>
                          {t('config.google.saveCustomerId')}
                        </button>
                      )}
                    </div>
                  )}
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#999' }}>
                    {t('config.google.customerIdHelp')}
                  </p>
                  {config?.metadata?.hasDeveloperToken === false && (
                    <div style={{ padding: '0.5rem', backgroundColor: '#fff3cd', borderRadius: '6px', fontSize: '0.8rem', color: '#856404', marginTop: '0.5rem' }}>
                      {t('config.google.noDeveloperToken')}
                    </div>
                  )}
                </div>
              )}

              {/* Action button */}
              {enabled ? (
                isConnected ? (
                  <button
                    onClick={() => handleDisconnect(id)}
                    disabled={disconnecting}
                    style={{
                      marginTop: 'auto', padding: '0.6rem 1rem',
                      backgroundColor: '#f8d7da', color: '#721c24', border: 'none',
                      borderRadius: '8px', cursor: disconnecting ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem', fontWeight: '500'
                    }}
                  >
                    {disconnecting ? '...' : t(`config.${id}.disconnect`)}
                  </button>
                ) : (
                  <button
                    onClick={() => handleConnect(id)}
                    disabled={connecting}
                    style={{
                      marginTop: 'auto', padding: '0.6rem 1rem',
                      backgroundColor: connecting ? '#ccc' : color, color: '#fff', border: 'none',
                      borderRadius: '8px', cursor: connecting ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem', fontWeight: '600'
                    }}
                  >
                    {connecting ? '...' : t(`config.${id}.connect`)}
                  </button>
                )
              ) : (
                <button disabled style={{
                  marginTop: 'auto', padding: '0.6rem 1rem', backgroundColor: '#e0e0e0', color: '#999',
                  border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '0.9rem', fontWeight: '500'
                }}>
                  {t(`config.${id}.connect`)} — {t(`config.${id}.comingSoon`)}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
